import type { Dispatch, SetStateAction } from 'react';

import { COMPONENT_NAME, EVENT_NAMES, PAGE_FLOW } from '../constants';

import type { PageFlow } from '../types';
import type { SubscriptionClientInterface } from '@web-platform/global-components-marketing-consent/types';
import type { Logger } from '@web-platform/global-components-types/utils';

type SubscriptionStatusParams = {
  handleClose: () => void;
  locale: string;
  market: string;
  subscriptionClient: SubscriptionClientInterface;
  logger: Logger;
  updatePageFlow: Dispatch<SetStateAction<PageFlow>>;
};

const EXCEEDED_TIMEOUT = 'EXCEEDED_TIMEOUT';

const useSubscriptionStatus = ({
  handleClose,
  locale,
  logger,
  market,
  subscriptionClient,
  updatePageFlow,
}: SubscriptionStatusParams) => {
  const checkSubscriptionStatus = async () => {
    const isSubscribed = subscriptionClient.isSubscribed();

    const waitTime = new Promise((resolve) => {
      setTimeout(() => resolve(EXCEEDED_TIMEOUT), 1500);
    });
    const completedPromise = await Promise.any([isSubscribed, waitTime]);
    await waitTime;
    if (completedPromise === EXCEEDED_TIMEOUT) {
      logger.logOperationalEvent({
        component: COMPONENT_NAME,
        eventName: EVENT_NAMES.READ_SUBSCRIPTION_TIMEOUT,
      });
    }
    if (completedPromise === EXCEEDED_TIMEOUT || (await isSubscribed) === false) {
      updatePageFlow(PAGE_FLOW.MARKETING_CONSENT);
    } else {
      handleClose();
    }
  };

  const updateSubscription = async (hasConsentedToMarketing: boolean) => {
    try {
      const subscriptionUpdate = subscriptionClient.updateSubscription(
        hasConsentedToMarketing,
        locale,
        market,
        COMPONENT_NAME,
      );

      const maxWaitTime = new Promise((resolve) => {
        setTimeout(() => resolve(EXCEEDED_TIMEOUT), 5000);
      });

      const completedPromise = await Promise.any([subscriptionUpdate, maxWaitTime]);

      if (completedPromise === EXCEEDED_TIMEOUT) {
        logger.logOperationalEvent({
          component: COMPONENT_NAME,
          eventName: EVENT_NAMES.UPDATE_SUBSCRIPTION_TIMEOUT,
        });
      } else {
        logger.logOperationalEvent({
          component: COMPONENT_NAME,
          eventName: hasConsentedToMarketing
            ? EVENT_NAMES.OPT_IN_SUCCESS
            : EVENT_NAMES.OPT_OUT_SUCCESS,
        });
      }
    } catch (error) {
      logger.logOperationalEvent({
        component: COMPONENT_NAME,
        eventName: EVENT_NAMES.UPDATE_SUBSCRIPTION_ERROR,
        errorMessage: `Received error from subscriptionClient: ${error}`,
        consentValue: String(hasConsentedToMarketing),
      });
    } finally {
      handleClose();
    }
  };

  return {
    checkSubscriptionStatus,
    updateSubscription,
  };
};

export default useSubscriptionStatus;
